import React, { useCallback, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import {
  type AppointmentType,
  AppointmentTypeModal,
  ColorDot,
  useAppointmentTypes,
  useDeleteAppointmentType,
} from '@features/appointment-types';
import { useCan } from '@features/user';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useModal } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { useOrgScheduleAccess } from '../hooks/useOrgScheduleAccess';
import { useOrgScheduleFilters } from '../hooks/useOrgScheduleFilters';

import CalendarSettingsMenu from './CalendarSettingsMenu';
import { MiniCalendar } from './MiniCalendar';
import { OrgScheduleFilterBar } from './OrgScheduleFilters';
import { OrgScheduleToggle } from './OrgScheduleToggle';
import { ScheduleViewToggle } from './ScheduleViewToggle';
import SyncGoogleCalendar from './SyncGoogleCalendar';
import { ViewModeSelector } from './ViewModeSelector';

/**
 * CalendarSidebar - Container component for sidebar content in the calendar view
 * Includes MiniCalendar and can be extended with additional sidebar features
 */
export const CalendarSidebar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { isOrgViewActive } = useOrgScheduleFilters();
  const isOrgScheduleActive = hasOrgScheduleAccess && isOrgViewActive;
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const { appointmentTypes, isLoading: isLoadingTypes } = useAppointmentTypes();
  const { deleteAppointmentType, isDeleting } = useDeleteAppointmentType();
  const { enqueueSnackbar } = useSnackbar();
  const { open: openModal } = useModal();

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedType, setSelectedType] = useState<AppointmentType | null>(null);
  const [hoveredTypeId, setHoveredTypeId] = useState<string | null>(null);
  const [deletingTypeId, setDeletingTypeId] = useState<string | null>(null);
  const [typeToDelete, setTypeToDelete] = useState<AppointmentType | null>(null);
  const [isTypesExpanded, setIsTypesExpanded] = useState(true);

  const handleEditType = useCallback((type: AppointmentType) => {
    setSelectedType(type);
    setShowEditModal(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedType(null);
  }, []);

  const handleDeleteClick = useCallback((e: React.MouseEvent, type: AppointmentType) => {
    e.stopPropagation();
    setTypeToDelete(type);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!typeToDelete) return;
    setDeletingTypeId(typeToDelete.encounter_type_id);
    const result = await deleteAppointmentType(typeToDelete.encounter_type_id);
    if (!result.success) {
      enqueueSnackbar(result.message || 'Failed to delete appointment type', {
        variant: 'error',
      });
    }
    setDeletingTypeId(null);
    setTypeToDelete(null);
  }, [typeToDelete, deleteAppointmentType, enqueueSnackbar]);

  const handleCancelDelete = useCallback(() => {
    setTypeToDelete(null);
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsTypesExpanded(prev => !prev);
  }, []);

  const handleManageAppointmentTypes = useCallback(() => {
    openModal(MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT, {
      source: 'calendar_sidebar',
      maxWidth: 'md',
      fullScreenOnMobile: true,
    });
  }, [openModal]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
        gap: 3,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Mini Calendar */}
        <MiniCalendar />

        {/* Org Schedule Toggle — hidden for clinician-only users */}
        {hasOrgScheduleAccess && (
          <Box
            sx={{ pl: 2, mb: 2, '& .MuiTabs-root': { width: '100%' }, '& .MuiTab-root': { flex: 1 } }}
          >
            <OrgScheduleToggle />
          </Box>
        )}

        {/* Manage Appointment Types — practice view only, above the provider filter */}
        {isOrgScheduleActive && canManageAppointmentTypes && (
          <Box sx={{ pl: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={handleManageAppointmentTypes}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                backgroundColor: 'white',
                color: '#535976',
                borderColor: 'rgba(0, 13, 77, 0.45)',
                '&:hover': { backgroundColor: 'white', borderColor: '#535976' },
              }}
            >
              Manage Appointment Types
            </Button>
          </Box>
        )}

        {/* Org Schedule Filters — shown only in org view */}
        {isOrgScheduleActive && <OrgScheduleFilterBar />}

        {/* Schedule View Toggle - only on mobile */}
        {isMobile && (
          <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ViewModeSelector variant="dropdown" />
            <ScheduleViewToggle />
          </Box>
        )}

        {/* Appointment Types Section — hidden when org view is active */}
        {!isOrgScheduleActive && (
          <Box sx={{ pl: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#535976',
                }}
              >
                Appointment Types
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {canManageAppointmentTypes && (
                  <Tooltip title="Manage practice appointment types" placement="top">
                    <IconButton
                      onClick={handleManageAppointmentTypes}
                      sx={{
                        backgroundColor: 'white',
                        '&:hover': { backgroundColor: 'white' },
                        width: 24,
                        height: 24,
                      }}
                      aria-label="Manage practice appointment types"
                    >
                      <SettingsIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={isTypesExpanded ? 'Collapse list' : 'Expand list'} placement="top">
                  <IconButton
                    onClick={handleToggleExpand}
                    sx={{
                      backgroundColor: 'white',
                      '&:hover': { backgroundColor: 'white' },
                      width: 24,
                      height: 24,
                    }}
                    aria-label={isTypesExpanded ? 'Collapse appointment types' : 'Expand appointment types'}
                  >
                    {isTypesExpanded ? (
                      <KeyboardArrowUpIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    ) : (
                      <KeyboardArrowDownIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Collapsible Appointment Types List */}
            <Collapse in={isTypesExpanded} timeout="auto">
              {isLoadingTypes ? (
                <Box sx={{ py: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </Box>
              ) : appointmentTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No appointment types yet
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    maxHeight: 240,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                >
                  {appointmentTypes.map(type => (
                    <Box
                      key={type.encounter_type_id}
                      onClick={() => handleEditType(type)}
                      onMouseEnter={() => setHoveredTypeId(type.encounter_type_id)}
                      onMouseLeave={() => setHoveredTypeId(null)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        px: 1,
                        mx: -1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'background-color 0.15s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ColorDot colorEnum={type.color} size={10} />
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontSize: '14px',
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {type.encounter_type}
                      </Typography>
                      {/* Org-owned types are managed through the practice
                          management modal — no inline delete here, even for
                          permission holders (keeps deletes on the org routes). */}
                      {hoveredTypeId === type.encounter_type_id &&
                        type.owner_scope !== 'organization' && (
                        <Tooltip title="Delete appointment type" placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(e, type)}
                            disabled={isDeleting || deletingTypeId === type.encounter_type_id}
                            sx={{
                              p: 0.25,
                              color: 'text.secondary',
                              '&:hover': { color: 'error.main' },
                            }}
                            aria-label={`Delete ${type.encounter_type}`}
                          >
                            {deletingTypeId === type.encounter_type_id ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Collapse>
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={!!typeToDelete}
          variant="warning"
          title="Delete Appointment Type?"
          message={`Are you sure you want to delete "${typeToDelete?.encounter_type}"? This action cannot be undone.`}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          confirmText="Delete"
          cancelText="Cancel"
          loading={isDeleting}
        />

        {/* Edit Appointment Type Modal */}
        {showEditModal && selectedType && (
          <AppointmentTypeModal
            open={showEditModal}
            onClose={handleCloseEditModal}
            type="edit"
            appointmentType={selectedType}
          />
        )}

      </Box>

      {/* Settings and Sync - only on mobile, at the bottom */}
      {isMobile && (
        <Box sx={{ pb: 12 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pl: 2,
            }}
          >
            <CalendarSettingsMenu variant="fullWidth" />
            {!isOrgScheduleActive && <SyncGoogleCalendar variant="fullWidth" />}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CalendarSidebar;
